import { makeStyles } from '@fluentui/react-components';

/**
 * The CSS half of the responsive pass (#53) — the parts a real browser's
 * layout engine decides, which is why they are `@media` rules rather than
 * anything driven from JavaScript.
 *
 * The query is written out literally because Griffel needs a literal at-rule
 * key; `MOBILE_BREAKPOINT_PX` in `useIsNarrowViewport.ts` is the same 700px,
 * and its test pins the two together.
 *
 * Shared rather than copied into six `useStyles` blocks so "single column
 * below the breakpoint" and "44px touch targets" mean exactly one thing
 * everywhere, and are changed in one place.
 */
export const useResponsiveStyles = makeStyles({
  /**
   * Touch targets, applied to a container rather than to each control: Fluent's
   * medium `Button` is 32px and its `Input` 32px, both under the 44px/40px the
   * ticket requires, and `size="large"` is not offered by every control this
   * app uses (`DatePicker` and `Combobox` among them). One descendant rule
   * covers every control in the subtree, including the ones Fluent renders
   * itself, and costs nothing above the breakpoint.
   */
  touchTargets: {
    '@media (max-width: 700px)': {
      '& button': { minHeight: '44px' },
      '& input': { minHeight: '40px' },
      '& textarea': { minHeight: '80px' },
    },
  },

  /** A multi-column flex or grid layout that collapses to one column on mobile. */
  singleColumn: {
    '@media (max-width: 700px)': {
      flexDirection: 'column',
      alignItems: 'stretch',
      gridTemplateColumns: '1fr',
      maxWidth: '100%',
    },
  },

  /**
   * A `DialogSurface` that fills the viewport below the breakpoint — the
   * ticket's "line editing on mobile opens a full-screen sheet". Fluent offers
   * no full-screen surface variant (only `fullscreen`-less width tokens), so
   * the surface's own inset/size/radius are overridden here.
   */
  mobileSheet: {
    '@media (max-width: 700px)': {
      inset: '0',
      width: '100vw',
      maxWidth: '100vw',
      height: '100dvh',
      maxHeight: '100dvh',
      margin: '0',
      borderRadius: '0',
      overflowY: 'auto',
    },
  },
});
